import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '../../config/firebase';
import styles from './Login.module.css';

/**
 * The gate. Same Firebase email+password sign-in as oneasia-corp (the shared
 * summerlin-6bdc4 project), so one account opens both sites. The NDA above the
 * form is the point of the screen as much as the credentials are.
 */
const Login = () => {
  const { t } = useTranslation('auth');

  // Force scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setSuccess(t('login.success'));
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 800);
    } catch (err) {
      switch ((err as { code?: string }).code) {
        case 'auth/invalid-email':
          setError(t('login.errors.invalidEmail'));
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError(t('login.errors.invalidCredential'));
          break;
        case 'auth/too-many-requests':
          setError(t('login.errors.tooMany'));
          break;
        case 'auth/network-request-failed':
          setError(t('login.errors.network'));
          break;
        default:
          setError(t('login.errors.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <div className={styles.logoSection}>
            <h1 className={styles.logo}>
              STACK<span>COMBO</span><i />
            </h1>
            <p className={styles.tagline}>{t('login.tagline')}</p>
          </div>

          <div className={styles.formSection}>
            <h2 className={styles.title}>{t('login.title')}</h2>

            <div className={styles.disclaimer}>
              <h3 className={styles.disclaimerTitle}>{t('login.disclaimer.title')}</h3>
              <p className={styles.disclaimerText}>
                <strong>{t('login.disclaimer.confidential')}</strong> {t('login.disclaimer.confidentialText')}
              </p>
              <p className={styles.disclaimerText}>
                <strong>{t('login.disclaimer.byProceeding')}</strong>
              </p>
              <ul className={styles.disclaimerList}>
                {(t('login.disclaimer.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className={styles.disclaimerText}>
                <strong>{t('login.disclaimer.legalNotice')}</strong> {t('login.disclaimer.legalText')}
              </p>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label htmlFor="email" className={styles.label}>{t('login.emailLabel')}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder={t('login.emailPlaceholder')}
                  autoComplete="username"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.label}>{t('login.passwordLabel')}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={styles.input}
                  placeholder={t('login.passwordPlaceholder')}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
              >
                {loading ? t('login.processingBtn') : t('login.signInBtn')}
              </button>
            </form>
          </div>

          <div className={styles.footer}>
            <p>{t('login.footer')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
